import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type {
	Customer,
	NewReportRequestArgs,
	ReportRequestRow,
	ReportRow,
} from '~/ogm-hub';
import { CustomerController, ReportController } from '~/ogm-hub';
import {
	submitOrderGuideDownloadRequest,
	submitStockMatrixRequest,
} from './ReportModalSlice';
import { submitOffOrderGuideSavingsRequest } from './ReportDateRangeModalSlice';
import type { IAppState } from '~/appstate';

export interface IPageState {
	customers: Customer[];
	currentCustomer: Customer;
	defaultCustomerId: number;
	reports: ReportRow[];
	reportsAvailableToCustomer: ReportRequestRow[];
	currentPage: number;
	numPages: number;
	loadingReports: boolean;
	loadingReportsAvailableToCustomer: boolean;
	submissionSuccess: boolean;
	submissionError: boolean;
	fetchError: boolean;
	lastCustomerId: number;
	showOGdownloadFiltersModal: boolean;
	showStockMatrixFiltersModal: boolean;
	showOffOrderGuideSavingsModal: boolean;
}

export const initialState: IPageState = {
	customers: [],
	currentCustomer: null,
	defaultCustomerId: null,
	currentPage: 1,
	numPages: 0,
	reports: [],
	reportsAvailableToCustomer: [],
	loadingReports: false,
	loadingReportsAvailableToCustomer: false,
	submissionSuccess: false,
	submissionError: false,
	fetchError: false,
	lastCustomerId: null,
	showOGdownloadFiltersModal: false,
	showStockMatrixFiltersModal: false,
	showOffOrderGuideSavingsModal: false,
};

export const loadReportsPageForCustomer = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportsPage/initializeForCustomer',
	async (customerId: number, { getState, dispatch }) => {
		const state = getState();
		const { customers, currentCustomer } = state.reportsPage.pageState;

		if (currentCustomer?.id !== customerId) {
			const customer =
				customers[customers.findIndex((cust) => cust.id === customerId)];

			dispatch(reportsPageSlice.actions.setCustomer(customer));
			dispatch(fetchReportsAvailableForCustomer(customerId));
			dispatch(fetchReports({ customerId, pageNumber: 1 }));
		}
	}
);

export const fetchCustomersForUser = createAsyncThunk<
	Customer[],
	void,
	{ rejectValue: string[] | Response }
>('reportPage/getCustomersForUser', async (_, { rejectWithValue }) => {
	try {
		const result = await unwrapFetch2(
			CustomerController.GetCustomersAssignedToUser()
		);
		return result;
	} catch (err) {
		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export interface IFetchReportsParameters {
	customerId: number;
	pageNumber: number;
}

export const fetchReports = createAsyncThunk<
	ReportRow[],
	IFetchReportsParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportsPage/fetchReports',
	async (params: IFetchReportsParameters, { dispatch, rejectWithValue }) => {
		try {
			const customerId = params.customerId === -1 ? null : params.customerId;
			const result = await unwrapFetch2(
				ReportController.GetCustomerReports(customerId, params.pageNumber, 40)
			);

			dispatch(reportsPageSlice.actions.setNumPages(result.totalPages));
			return result.results;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const loadPageNumber = createAsyncThunk<
	void,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>('reportPage/loadPage', async (newPageNum, { getState, dispatch }) => {
	const state = getState();
	const { currentPage, numPages, currentCustomer } =
		state.reportsPage.pageState;

	if (currentPage !== newPageNum && newPageNum <= numPages && newPageNum > 0) {
		dispatch(
			fetchReports({ customerId: currentCustomer.id, pageNumber: newPageNum })
		);
		dispatch(reportsPageSlice.actions.setCurrentPage(newPageNum));
	}
});

export const fetchReportsAvailableForCustomer = createAsyncThunk<
	ReportRequestRow[],
	number,
	{ rejectValue: string[] | Response }
>(
	'reportsPage/fetchReportsAvailableForCustomer',
	async (customerId: number, { rejectWithValue }) => {
		try {
			const result = await unwrapFetch2(
				ReportController.GetRequestableReports(customerId)
			);

			return result.reportRequests;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const requestReport = createAsyncThunk<
	ReportRequestRow,
	NewReportRequestArgs,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'reportsPage/requestReport',
	async (requestArgs: NewReportRequestArgs, { rejectWithValue }) => {
		try {
			const response = await unwrapFetch2(
				ReportController.NewReportRequest(requestArgs)
			);
			if (response.errors != null && response.errors.length > 0) {
				return rejectWithValue(response.errors);
			}
			return response.reportRequestRow;
		} catch (err) {
			if (err instanceof ApiRequestError) {
				return rejectWithValue(err.errors);
			}
			if (err instanceof ApiServerError) {
				return rejectWithValue(err.response);
			}
			throw err;
		}
	}
);

export const reportsPageSlice = createSlice({
	name: 'reportsPageSlice',
	initialState,
	reducers: {
		closeOGdownloadFiltersModal: (state) => {
			state.showOGdownloadFiltersModal = false;
		},
		openOGdownloadFiltersModal: (state) => {
			state.showOGdownloadFiltersModal = true;
		},
		closeStockMatrixFiltersModal: (state) => {
			state.showStockMatrixFiltersModal = false;
		},
		openStockMatrixFiltersModal: (state) => {
			state.showStockMatrixFiltersModal = true;
		},
		openOffOrderGuideSavingsModal: (state) => {
			state.showOffOrderGuideSavingsModal = true;
		},
		closeOffOrderGuideSavingsModal: (state) => {
			state.showOffOrderGuideSavingsModal = false;
		},
		initializeCustomer: (state, action) => {
			state.currentPage = 1;
			state.numPages = action.payload.numPages;
			state.reports = action.payload.reports;
		},
		addReportPage: (state, action) => {
			state.reports[action.payload.pageNumber] = action.payload;
		},
		setCustomer: (state, action) => {
			state.currentCustomer = action.payload;
		},
		setNumPages: (state, action) => {
			state.numPages = action.payload;
		},
		dismissSubmitError: (state) => {
			state.submissionError = false;
		},
		dismissSuccess: (state) => {
			state.submissionSuccess = false;
		},
		dismissFetchError: (state) => {
			state.fetchError = false;
		},
		setDefaultCustomerId: (state, action) => {
			state.defaultCustomerId = action.payload;
		},
		setCurrentPage: (state, action) => {
			state.currentPage = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchReportsAvailableForCustomer.pending, (state) => {
			state.loadingReportsAvailableToCustomer = true;
		});
		builder.addCase(
			fetchReportsAvailableForCustomer.fulfilled,
			(state, action) => {
				if (
					action.payload.length > 0 &&
					action.payload[0].customerId === state.currentCustomer?.id
				) {
					state.reportsAvailableToCustomer = action.payload;
				}
				state.loadingReportsAvailableToCustomer = false;
			}
		);
		builder.addCase(fetchReportsAvailableForCustomer.rejected, (state) => {
			state.loadingReportsAvailableToCustomer = false;
			state.fetchError = true;
		});
		builder.addCase(fetchReports.pending, (state) => {
			state.loadingReports = true;
		});
		builder.addCase(fetchReports.fulfilled, (state, action) => {
			state.loadingReports = false;
			state.reports = action.payload;
		});
		builder.addCase(fetchReports.rejected, (state) => {
			state.loadingReports = false;
			state.fetchError = true;
		});
		builder.addCase(requestReport.fulfilled, (state, action) => {
			state.reportsAvailableToCustomer = insertReportRequestRow(
				state.reportsAvailableToCustomer,
				action.payload
			);
			state.submissionSuccess = true;
		});
		builder.addCase(requestReport.rejected, (state) => {
			state.submissionError = true;
		});
		builder.addCase(fetchCustomersForUser.fulfilled, (state, action) => {
			state.customers = action.payload;
		});
		builder.addCase(fetchCustomersForUser.rejected, (state) => {
			state.fetchError = true;
		});
		builder.addCase(
			submitOrderGuideDownloadRequest.fulfilled,
			(state, action) => {
				state.reportsAvailableToCustomer = insertReportRequestRow(
					state.reportsAvailableToCustomer,
					action.payload
				);
				state.showOGdownloadFiltersModal = false;
			}
		);
		builder.addCase(submitStockMatrixRequest.fulfilled, (state, action) => {
			state.reportsAvailableToCustomer = insertReportRequestRow(
				state.reportsAvailableToCustomer,
				action.payload
			);
			state.showStockMatrixFiltersModal = false;
		});
		builder.addCase(
			submitOffOrderGuideSavingsRequest.fulfilled,
			(state, action) => {
				state.reportsAvailableToCustomer = insertReportRequestRow(
					state.reportsAvailableToCustomer,
					action.payload
				);
				state.showOffOrderGuideSavingsModal = false;
			}
		);
	},
});

const insertReportRequestRow = (
	availableReports: ReportRequestRow[],
	requestedReport: ReportRequestRow
) => {
	return availableReports.map((currentReport) => {
		return currentReport.reportTypeId === requestedReport.reportTypeId
			? requestedReport
			: currentReport;
	});
};

export const {
	openOGdownloadFiltersModal,
	closeOGdownloadFiltersModal,
	openStockMatrixFiltersModal,
	closeStockMatrixFiltersModal,
	openOffOrderGuideSavingsModal,
	closeOffOrderGuideSavingsModal,
	dismissFetchError,
	dismissSubmitError,
	dismissSuccess,
	setDefaultCustomerId,
} = reportsPageSlice.actions;
